import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useKey, useUpdate } from 'react-use';
import { cn } from '../classname';
import { i18n } from '../i18n/bundle';
import { logger } from '../logger';
import { useBooleanState } from '../react-utils/hooks';
import { ToasterContext } from '../react-utils/toaster';
import { useSticky } from '../react-utils/useSticky';
import { isMac } from '../utils/platform';
import { HorizontalDrag } from './HorizontalDrag';
import { MarkupEditorView } from './MarkupEditorView';
import { SplitModeView } from './SplitModeView';
import { WysiwygEditorView } from './WysiwygEditorView';
import { mHiddenData, mToolbarConfig } from './config/markup';
import { wHiddenData, wToolbarConfig } from './config/wysiwyg';
import { useYfmEditorContext } from './context';
import { EditorSettings } from './settings';
import { stickyCn } from './sticky';
import '../styles/styles.css';
import './YfmEditorView.css'; // eslint-disable-line import/order
export const cnEditorComponent = cn('editor-component');
const b = cnEditorComponent;
export const YfmEditorView = React.forwardRef((props, ref) => {
    var _a, _b;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    const [showPreview, , unsetShowPreview, toggleShowPreview] = useBooleanState(false);
    const context = useYfmEditorContext();
    const editor = ((_a = props.editor) !== null && _a !== void 0 ? _a : context);
    if (!editor)
        throw new Error('[YfmEditorView]: an instance of the editor must be passed through the props or context');
    const { autofocus, className, settingsVisible = true, markupToolbarConfig = mToolbarConfig, wysiwygToolbarConfig = wToolbarConfig, markupHiddenActionsConfig = mHiddenData, wysiwygHiddenActionsConfig = wHiddenData, toaster, stickyToolbar, } = props;
    const rerender = useUpdate();
    React.useLayoutEffect(() => {
        editor.on('rerender', rerender);
        return () => {
            editor.off('rerender', rerender);
        };
    }, [editor, rerender]);
    const onModeChange = React.useCallback((type) => {
        editor.changeEditorType({ type, reason: 'settings' });
        unsetShowPreview();
    }, [editor, unsetShowPreview]);
    const onToolbarVisibilityChange = React.useCallback((visible) => {
        editor.changeToolbarVisibility({ visible });
    }, [editor]);
    const onSplitModeChange = React.useCallback((splitModeEnabled) => {
        unsetShowPreview();
        editor.changeSplitModeEnabled({ splitModeEnabled });
    }, [editor, unsetShowPreview]);
    const onShowPreviewChange = React.useCallback((showPreviewValue) => {
        editor.changeSplitModeEnabled({ splitModeEnabled: false });
        if (showPreviewValue !== showPreview)
            toggleShowPreview();
    }, [editor, showPreview, toggleShowPreview]);
    const editorType = editor.currentType;
    const markupSplitMode = editor.splitModeEnabled && editor.splitMode && editorType === 'markup';
    const canRenderPreview = Boolean(editor.renderPreview && editorType === 'markup' && !editor.splitModeEnabled);
    useKey((e) => canRenderPreview && isPreviewKeyDown(e), () => onShowPreviewChange(!showPreview), { event: 'keydown' }, [showPreview, editorType, onShowPreviewChange, canRenderPreview]);
    const editorWrapperRef = useRef(null);
    const splitModeViewWrapperRef = useRef(null);
    const settings = useMemo(() => (React.createElement(Settings, { mode: editorType, onModeChange: onModeChange, toolbarVisibility: editor.toolbarVisible && !showPreview, onToolbarVisibilityChange: onToolbarVisibilityChange, onSplitModeChange: onSplitModeChange, splitModeEnabled: editor.splitModeEnabled, splitMode: editor.splitMode, stickyToolbar: stickyToolbar, onShowPreviewChange: onShowPreviewChange, showPreview: showPreview, renderPreviewButton: canRenderPreview })), [
        canRenderPreview,
        stickyToolbar,
        editor.splitMode,
        editor.splitModeEnabled,
        editor.toolbarVisible,
        editorType,
        onModeChange,
        onShowPreviewChange,
        onSplitModeChange,
        onToolbarVisibilityChange,
        showPreview,
    ]);
    return (React.createElement(ErrorBoundary, { onError: (e) => {
            logger.error(e);
        }, fallbackRender: ({ error, resetErrorBoundary }) => {
            toaster.add({
                theme: 'danger',
                name: 'yfm-editor',
                title: i18n('error-title'),
                content: error.message,
            });
            setTimeout(() => {
                resetErrorBoundary();
                editor.changeEditorType({ type: 'markup', reason: 'error-boundary' });
            });
            return null;
        } },
        React.createElement(ToasterContext.Provider, { value: toaster },
            React.createElement("div", { ref: ref, className: b({
                    settings: settingsVisible,
                    split: markupSplitMode && editor.splitMode,
                }, [className]) },
                React.createElement("div", { className: b('editor-wrapper'), ref: editorWrapperRef }, showPreview ? (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: b('preview-wrapper') }, (_b = editor.renderPreview) === null || _b === void 0 ? void 0 : _b.call(editor, {
                        getValue: editor.getValue,
                        mode: 'preview',
                    })),
                    settings)) : (React.createElement(React.Fragment, null,
                    editorType === 'wysiwyg' && (React.createElement(WysiwygEditorView, { editor: editor, autofocus: autofocus, settingsVisible: settingsVisible, toolbarConfig: wysiwygToolbarConfig, toolbarVisible: editor.toolbarVisible, hiddenActionsConfig: wysiwygHiddenActionsConfig, className: b('editor', { type: editorType }), toolbarClassName: b('toolbar'), stickyToolbar: stickyToolbar }, editor.toolbarVisible && settingsVisible && settings)),
                    editorType === 'markup' && (React.createElement(MarkupEditorView, { editor: editor, autofocus: autofocus, settingsVisible: settingsVisible, toolbarConfig: markupToolbarConfig, toolbarVisible: editor.toolbarVisible, splitMode: editor.splitMode, splitModeEnabled: editor.splitModeEnabled, hiddenActionsConfig: markupHiddenActionsConfig, className: b('editor', { type: editorType }), toolbarClassName: b('toolbar'), stickyToolbar: stickyToolbar }, editor.toolbarVisible && settingsVisible && settings)),
                    !editor.toolbarVisible && settingsVisible && settings))),
                markupSplitMode && (React.createElement(React.Fragment, null,
                    editor.splitMode === 'horizontal' ? (React.createElement(HorizontalDrag, { editor: editor, isMounted: isMounted, leftElRef: editorWrapperRef, rightElRef: splitModeViewWrapperRef, wrapperRef: ref })) : (React.createElement("div", { className: b('resizer') })),
                    React.createElement(SplitModeView, { editor: editor, ref: splitModeViewWrapperRef })))))));
});
YfmEditorView.displayName = 'YfmEditorView';
export function Settings(props) {
    const wrapperRef = useRef(null);
    const isSticky = useSticky(wrapperRef) && props.toolbarVisibility && props.stickyToolbar;
    return (React.createElement("div", { className: b('settings-wrapper') },
        React.createElement("div", { ref: wrapperRef, className: stickyCn.settings({
                withToolbar: props.toolbarVisibility,
                stickyActive: isSticky,
            }) },
            React.createElement(EditorSettings, Object.assign({}, props)))));
}
function isPreviewKeyDown(e) {
    const modKey = isMac() ? e.metaKey : e.ctrlKey;
    return modKey && e.shiftKey && e.code === 'KeyP';
}
